import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const AnimatedMeshBg = ({ intensity = 1 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId = null;
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const drawMesh = () => {
      // Clear with dark navy
      ctx.fillStyle = 'rgba(15, 20, 25, 0.95)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add subtle gradient overlay
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, 'rgba(37, 99, 235, 0.05)');
      gradient.addColorStop(0.5, 'rgba(15, 20, 25, 0)');
      gradient.addColorStop(1, 'rgba(14, 165, 233, 0.05)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const gridSize = 120;
      const cols = Math.ceil(canvas.width / gridSize) + 2;
      const rows = Math.ceil(canvas.height / gridSize) + 2;
      const nodes = [];

      // Generate grid nodes with wave animation
      for (let y = -1; y < rows; y++) {
        for (let x = -1; x < cols; x++) {
          const baseX = x * gridSize;
          const baseY = y * gridSize;

          // Create wave effect
          const wave =
            Math.sin((x + time * 0.005) * 0.05) * 30 +
            Math.cos((y + time * 0.005) * 0.05) * 30;

          const nodeX = baseX + wave;
          const nodeY = baseY + Math.sin((x + y + time * 0.003) * 0.02) * 20;

          nodes.push({ x: nodeX, y: nodeY, baseX, baseY, col: x, row: y });
        }
      }

      // Draw connecting lines
      ctx.strokeStyle = 'rgba(37, 99, 235, 0.2)';
      ctx.lineWidth = 1;

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];

        // Connect to right neighbor
        const rightIdx = nodes.findIndex(
          (n) => n.col === node.col + 1 && n.row === node.row
        );
        if (rightIdx !== -1) {
          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(nodes[rightIdx].x, nodes[rightIdx].y);
          ctx.stroke();
        }

        // Connect to bottom neighbor
        const bottomIdx = nodes.findIndex(
          (n) => n.col === node.col && n.row === node.row + 1
        );
        if (bottomIdx !== -1) {
          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(nodes[bottomIdx].x, nodes[bottomIdx].y);
          ctx.stroke();
        }
      }

      // Draw glowing nodes
      nodes.forEach((node) => {
        const distance = Math.sqrt(
          Math.pow(canvas.width / 2 - node.x, 2) +
            Math.pow(canvas.height / 2 - node.y, 2)
        );
        const maxDistance = Math.sqrt(
          Math.pow(canvas.width / 2, 2) + Math.pow(canvas.height / 2, 2)
        );
        const opacity = Math.max(0.1, 1 - distance / maxDistance);

        // Node glow
        ctx.fillStyle = `rgba(14, 165, 233, ${opacity * 0.6})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, 2.5, 0, Math.PI * 2);
        ctx.fill();

        // Node highlight
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.3})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, 1, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw moving particles along lines
      for (let i = 0; i < 15; i++) {
        const nodeIdx = Math.floor((time * 0.5 + i * 10) % nodes.length);
        const node = nodes[nodeIdx];

        if (node) {
          const pulseSize = Math.sin(time * 0.02 + i) * 1.5 + 3;
          ctx.fillStyle = `rgba(14, 165, 233, ${0.8 - pulseSize / 6})`;
          ctx.beginPath();
          ctx.arc(node.x, node.y, pulseSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      time++;
      animationId = requestAnimationFrame(drawMesh);
    };

    drawMesh();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [intensity]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};

export default AnimatedMeshBg;
